import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ===================== ANIMATIONS =====================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.95) translateY(8px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-6px) rotate(1deg); }
  75% { transform: translateY(4px) rotate(-1deg); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const countUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.15); }
  50% { box-shadow: 0 0 0 8px rgba(2, 132, 199, 0); }
`;

const coinFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(5deg); }
  50% { transform: translateY(-4px) rotate(-3deg); }
  75% { transform: translateY(-10px) rotate(2deg); }
`;

// ===================== LAYOUT =====================
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #f0f7ff 0%, #e8f1fb 30%, #f5f8fc 60%, #edf4ff 100%);
  padding: 32px 24px 80px;
  @media (max-width: 768px) { padding: 20px 14px 60px; }
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

// ===================== HERO =====================
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 32px;
  border-radius: 22px;
  overflow: hidden;
  background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 35%, #0284c7 65%, #0ea5e9 100%);
  padding: 48px 52px;
  box-shadow:
    0 4px 6px rgba(3, 105, 161, 0.08),
    0 20px 50px rgba(3, 105, 161, 0.15);

  @media (max-width: 768px) {
    padding: 36px 24px;
    border-radius: 18px;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  svg { position: absolute; width: 100%; height: 100%; }
`;

const HeroGrid = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  @media (max-width: 768px) { flex-direction: column; text-align: center; }
`;

const HeroLeft = styled.div`
  flex: 1;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
  padding: 6px 16px 6px 8px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.5s ease 0.1s backwards;
`;

const BadgeDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #67e8f9;
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: #67e8f9;
    animation: ${ripple} 2s ease-out infinite;
  }
`;

const BadgeText = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.6px;
  margin: 0 0 12px;
  line-height: 1.2;
  animation: ${fadeIn} 0.5s ease 0.15s backwards;
  @media (max-width: 640px) { font-size: 1.6rem; }
`;

const HeroDescription = styled.p`
  font-size: 1.02rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.65;
  font-weight: 430;
  max-width: 440px;
  animation: ${fadeIn} 0.5s ease 0.2s backwards;
  @media (max-width: 768px) { max-width: 100%; }
`;

const HeroVisual = styled.div`
  flex-shrink: 0;
  width: 180px;
  height: 150px;
  animation: ${float} 6s ease-in-out infinite, ${fadeIn} 0.6s ease 0.3s backwards;
  @media (max-width: 768px) { width: 140px; height: 120px; }
`;

// ===================== EARNINGS CARDS =====================
const EarningsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EarningCard = styled.div`
  background: white;
  border-radius: 18px;
  padding: 28px 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.04);
  position: relative;
  overflow: hidden;
  animation: ${popIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  animation-delay: ${p => p.delay || '0s'};
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${p => p.accent || 'linear-gradient(90deg, #0284c7, #0ea5e9)'};
    border-radius: 4px 4px 0 0;
  }

  ${p => p.primary && `
    background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0284c7 100%);
    border: none;
    &::before { display: none; }
    &:hover {
      box-shadow: 0 8px 30px rgba(2, 132, 199, 0.3);
    }
  `}
`;

const CardIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: ${p => p.bg || 'linear-gradient(135deg, #e0f2fe, #bae6fd)'};
  transition: transform 0.3s ease;

  /* Avoid component selectors requiring Emotion plugin; keep hover effect on wrapper if desired */
  &:hover {
    transform: scale(1.08);
  }

  svg {
    width: 22px;
    height: 22px;
    color: ${p => p.iconColor || '#0284c7'};
  }
`;

const CardLabel = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0 0 8px;
  color: ${p => p.light ? 'rgba(255,255,255,0.7)' : '#94a3b8'};
`;

const CardValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  color: ${p => p.light ? 'white' : '#0c4a6e'};
  animation: ${countUp} 0.5s ease backwards;
  animation-delay: ${p => p.delay || '0.2s'};

  @media (max-width: 640px) { font-size: 1.6rem; }
`;

const CardNote = styled.p`
  font-size: 0.78rem;
  font-weight: 500;
  margin: 0;
  color: ${p => p.light ? 'rgba(255,255,255,0.6)' : '#94a3b8'};
`;

// ===================== INFO BANNER =====================
const InfoBanner = styled.div`
  background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
  border: 2px solid #7dd3fc;
  border-radius: 14px;
  padding: 18px 22px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 14px;
  animation: ${fadeIn} 0.4s ease 0.15s backwards;

  .icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #0284c7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg { width: 20px; height: 20px; color: white; }
  }

  .text {
    flex: 1;
    font-size: 0.9rem;
    color: #0c4a6e;
    font-weight: 500;
    line-height: 1.5;
    strong { font-weight: 700; color: #0369a1; }
  }
`;

// ===================== SECTION CARDS =====================
const SectionCard = styled.div`
  background: white;
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.04);
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease backwards;
  animation-delay: ${p => p.delay || '0s'};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f9ff;

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${p => p.iconBg || 'linear-gradient(135deg, #e0f2fe, #bae6fd)'};
    display: flex;
    align-items: center;
    justify-content: center;
    svg { width: 18px; height: 18px; color: ${p => p.iconColor || '#0284c7'}; }
  }

  h2 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0c4a6e;
    margin: 0;
  }
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
  position: relative;
  animation: ${slideRight} 0.35s ease backwards;
  animation-delay: ${p => p.delay || '0s'};

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 17px;
    bottom: -1px;
    width: 2px;
    height: 14px;
    background: #e0f2fe;
    transform: translateY(100%);
    z-index: 1;
  }
`;

const StepNumber = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${p => p.active ? 'linear-gradient(135deg, #0284c7, #0369a1)' : '#f0f9ff'};
  color: ${p => p.active ? 'white' : '#0284c7'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.82rem;
  flex-shrink: 0;
  transition: all 0.2s ease;
  border: 2px solid ${p => p.active ? 'transparent' : '#bae6fd'};
`;

const StepContent = styled.div`
  flex: 1;
  .step-title {
    font-size: 0.9rem;
    font-weight: 650;
    color: #1e293b;
    margin: 0 0 4px;
    line-height: 1.35;
  }
  .step-desc {
    font-size: 0.82rem;
    color: #94a3b8;
    margin: 0;
    line-height: 1.5;
    font-weight: 450;
  }
`;

const PaymentMethodCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 14px;
  border: 1px solid #bae6fd;
  animation: ${popIn} 0.35s ease 0.1s backwards;

  .method-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #0284c7, #0369a1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg { width: 24px; height: 24px; color: white; }
  }

  .method-info {
    flex: 1;
    .title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #0c4a6e;
      margin: 0 0 6px;
    }
    .desc {
      font-size: 0.85rem;
      color: #475569;
      margin: 0;
      line-height: 1.55;
      font-weight: 450;
    }
    .providers {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      flex-wrap: wrap;
    }
  }
`;

const ProviderChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  background: white;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 650;
  color: #0369a1;
  border: 1px solid #bae6fd;
  transition: all 0.2s ease;
  &:hover { transform: translateY(-1px); background: #f0f9ff; }
`;

// ===================== LOADING =====================
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  animation: ${fadeIn} 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid #e0f2fe;
  border-top-color: #0284c7;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 0.9rem;
  color: #94a3b8;
  font-weight: 500;
`;

const Bone = styled.div`
  height: ${p => p.h || '14px'};
  width: ${p => p.w || '100px'};
  border-radius: ${p => p.r || '6px'};
  background: linear-gradient(90deg, #e0f2fe 25%, #bae6fd 50%, #e0f2fe 75%);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s infinite linear;
`;

// ===================== SVG ICONS =====================
const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const TrendUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const InfoCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const BarChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const HeroBackgroundSVG = () => (
  <svg viewBox="0 0 800 200" preserveAspectRatio="none">
    <defs>
      <pattern id="heroGridEarnings" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.04" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#heroGridEarnings)" />
    <circle cx="650" cy="40" r="60" fill="white" fillOpacity="0.03" />
    <circle cx="700" cy="140" r="40" fill="white" fillOpacity="0.04" />
    <circle cx="100" cy="160" r="80" fill="white" fillOpacity="0.02" />
  </svg>
);

const EarningsIllustration = () => (
  <svg viewBox="0 0 180 150" fill="none">
    {/* Wallet shape */}
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; 0,-4; 0,0" dur="4s" repeatCount="indefinite" />
      <rect x="25" y="40" width="70" height="55" rx="10" fill="white" fillOpacity="0.15" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" />
      <rect x="25" y="40" width="70" height="18" rx="10" fill="white" fillOpacity="0.08" />
      <circle cx="80" cy="70" r="8" fill="white" fillOpacity="0.12" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
      <circle cx="80" cy="70" r="3" fill="#67e8f9" fillOpacity="0.6" />
    </g>

    {/* Floating coins */}
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; 2,-6; 0,0" dur="3s" repeatCount="indefinite" />
      <circle cx="120" cy="35" r="12" fill="#67e8f9" fillOpacity="0.25" stroke="#67e8f9" strokeOpacity="0.4" strokeWidth="1" />
      <text x="120" y="39" textAnchor="middle" fill="white" fillOpacity="0.7" fontSize="10" fontWeight="bold">₵</text>
    </g>

    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; -3,-4; 0,0" dur="3.5s" repeatCount="indefinite" />
      <circle cx="140" cy="60" r="9" fill="#67e8f9" fillOpacity="0.2" stroke="#67e8f9" strokeOpacity="0.35" strokeWidth="1" />
      <text x="140" y="63" textAnchor="middle" fill="white" fillOpacity="0.6" fontSize="8" fontWeight="bold">₵</text>
    </g>

    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; 1,-8; 0,0" dur="4.2s" repeatCount="indefinite" />
      <circle cx="155" cy="85" r="7" fill="#67e8f9" fillOpacity="0.15" stroke="#67e8f9" strokeOpacity="0.3" strokeWidth="1" />
      <text x="155" y="88" textAnchor="middle" fill="white" fillOpacity="0.5" fontSize="7" fontWeight="bold">₵</text>
    </g>

    {/* Trend arrow */}
    <path d="M30 110 L55 100 L80 105 L105 90" stroke="#67e8f9" strokeOpacity="0.5" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 3">
      <animate attributeName="stroke-dashoffset" values="0;-7" dur="1s" repeatCount="indefinite" />
    </path>
    <polygon points="105,87 108,93 102,93" fill="#67e8f9" fillOpacity="0.5">
      <animate attributeName="fillOpacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
    </polygon>

    {/* Small bars */}
    <rect x="120" y="105" width="8" height="20" rx="2" fill="white" fillOpacity="0.15">
      <animate attributeName="height" values="20;28;20" dur="2.5s" repeatCount="indefinite" />
      <animate attributeName="y" values="105;97;105" dur="2.5s" repeatCount="indefinite" />
    </rect>
    <rect x="132" y="100" width="8" height="25" rx="2" fill="white" fillOpacity="0.2">
      <animate attributeName="height" values="25;18;25" dur="3s" repeatCount="indefinite" />
      <animate attributeName="y" values="100;107;100" dur="3s" repeatCount="indefinite" />
    </rect>
    <rect x="144" y="95" width="8" height="30" rx="2" fill="#67e8f9" fillOpacity="0.25">
      <animate attributeName="height" values="30;35;30" dur="2.8s" repeatCount="indefinite" />
      <animate attributeName="y" values="95;90;95" dur="2.8s" repeatCount="indefinite" />
    </rect>

    <line x1="118" y1="128" x2="155" y2="128" stroke="white" strokeOpacity="0.2" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

// ===================== COMPONENT =====================
const Earnings = () => {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadEarnings(); }, []);

  const loadEarnings = async () => {
    try {
      const response = await axios.get(`${API_URL}/practitioner/earnings`);
      setEarnings(response.data.earnings);
    } catch (error) {
      console.error('Failed to load earnings:', error);
      setEarnings({ total: 0, pending: 0, withdrawn: 0, available: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingWrapper>
            <LoadingSpinner />
            <LoadingText>Loading earnings data…</LoadingText>
          </LoadingWrapper>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        {/* Hero */}
        <HeroSection>
          <HeroBackground><HeroBackgroundSVG /></HeroBackground>
          <HeroGrid>
            <HeroLeft>
              <HeroBadge>
                <BadgeDot />
                <BadgeText>Revenue Tracking</BadgeText>
              </HeroBadge>
              <HeroTitle>My Earnings</HeroTitle>
              <HeroDescription>
                Track your earnings from client assessments, monitor pending payouts, and manage your payment history.
              </HeroDescription>
            </HeroLeft>
            <HeroVisual>
              <EarningsIllustration />
            </HeroVisual>
          </HeroGrid>
        </HeroSection>

        {/* Info Banner */}
        <InfoBanner>
          <div className="icon-wrap"><InfoCircleIcon /></div>
          <div className="text">
            You receive <strong>20%</strong> of each assessment fee. Payouts are processed by the admin team on a regular basis.
          </div>
        </InfoBanner>

        {/* Earnings Cards */}
        <EarningsGrid>
          <EarningCard primary delay="0.1s">
            <CardIconWrap bg="rgba(255,255,255,0.15)" iconColor="white">
              <TrendUpIcon />
            </CardIconWrap>
            <CardLabel light>Total Earned</CardLabel>
            <CardValue light delay="0.25s">{formatCurrency(earnings?.total)}</CardValue>
            <CardNote light>Lifetime earnings</CardNote>
          </EarningCard>

          <EarningCard delay="0.15s" accent="linear-gradient(90deg, #f59e0b, #fbbf24)">
            <CardIconWrap bg="linear-gradient(135deg, #fef3c7, #fde68a)" iconColor="#d97706">
              <ClockIcon />
            </CardIconWrap>
            <CardLabel>Pending Payout</CardLabel>
            <CardValue delay="0.3s">{formatCurrency(earnings?.pending)}</CardValue>
            <CardNote>Awaiting admin payment</CardNote>
          </EarningCard>

          <EarningCard delay="0.2s" accent="linear-gradient(90deg, #10b981, #34d399)">
            <CardIconWrap bg="linear-gradient(135deg, #d1fae5, #a7f3d0)" iconColor="#059669">
              <CheckCircleIcon />
            </CardIconWrap>
            <CardLabel>Already Paid</CardLabel>
            <CardValue delay="0.35s">{formatCurrency(earnings?.withdrawn)}</CardValue>
            <CardNote>Received payments</CardNote>
          </EarningCard>
        </EarningsGrid>

        {/* How It Works */}
        <SectionCard delay="0.2s">
          <SectionHeader iconBg="linear-gradient(135deg, #e0f2fe, #bae6fd)" iconColor="#0284c7">
            <div className="section-icon"><BarChartIcon /></div>
            <h2>How It Works</h2>
          </SectionHeader>
          <StepsList>
            <StepItem delay="0.25s">
              <StepNumber active>1</StepNumber>
              <StepContent>
                <p className="step-title">Client Pays for Assessment</p>
                <p className="step-desc">When a client pays for their assessment results, you automatically earn 20% of the fee.</p>
              </StepContent>
            </StepItem>
            <StepItem delay="0.3s">
              <StepNumber>2</StepNumber>
              <StepContent>
                <p className="step-title">Earnings Appear as Pending</p>
                <p className="step-desc">Your share appears in the "Pending Payout" section, tracked and ready for processing.</p>
              </StepContent>
            </StepItem>
            <StepItem delay="0.35s">
              <StepNumber>3</StepNumber>
              <StepContent>
                <p className="step-title">Admin Processes Payout</p>
                <p className="step-desc">The admin team reviews and processes payouts on a periodic basis.</p>
              </StepContent>
            </StepItem>
            <StepItem delay="0.4s">
              <StepNumber>4</StepNumber>
              <StepContent>
                <p className="step-title">Payment Received</p>
                <p className="step-desc">Once paid, the amount moves to "Already Paid" and you receive your funds.</p>
              </StepContent>
            </StepItem>
            <StepItem delay="0.45s">
              <StepNumber>5</StepNumber>
              <StepContent>
                <p className="step-title">Need Help?</p>
                <p className="step-desc">Contact the admin team for any payout inquiries or payment-related questions.</p>
              </StepContent>
            </StepItem>
          </StepsList>
        </SectionCard>

        {/* Payment Method */}
        <SectionCard delay="0.3s">
          <SectionHeader iconBg="linear-gradient(135deg, #e0f2fe, #bae6fd)" iconColor="#0284c7">
            <div className="section-icon"><PhoneIcon /></div>
            <h2>Payment Method</h2>
          </SectionHeader>
          <PaymentMethodCard>
            <div className="method-icon"><PhoneIcon /></div>
            <div className="method-info">
              <p className="title">Mobile Money</p>
              <p className="desc">
                Payouts are typically made via Mobile Money. Ensure your phone number in your profile is correct for receiving payments.
              </p>
              <div className="providers">
                <ProviderChip>📱 MTN MoMo</ProviderChip>
                <ProviderChip>📱 Vodafone Cash</ProviderChip>
                <ProviderChip>📱 AirtelTigo Money</ProviderChip>
              </div>
            </div>
          </PaymentMethodCard>
        </SectionCard>
      </Container>
    </PageWrapper>
  );
};

export default Earnings;
